import moment from 'moment';

/**
 * Checks whether the last two State of Charge (SoC) readings
 * in a 24-hour dataset are recent enough to be considered valid.
 *
 * The most recent reading must be within the last 35 minutes,
 * and the second most recent reading must be within the last 65 minutes.
 *
 * @param {Array} twentyFourHoursOfData - An array of SoC data objects, each containing an `end_date` property (UNIX timestamp in seconds).
 * @returns {boolean} - True if both of the last two readings are recent, otherwise false.
 */
export const hasLastTwoSocReadings = (twentyFourHoursOfData) => {
  if (!twentyFourHoursOfData || twentyFourHoursOfData.length < 2) {
    return false;
  }

  const now = moment();
  const lastRecordTime = moment.unix(twentyFourHoursOfData[twentyFourHoursOfData.length - 1].end_date);
  const secondLastRecordTime = moment.unix(twentyFourHoursOfData[twentyFourHoursOfData.length - 2].end_date);

  const isLastRecordRecent = now.diff(lastRecordTime, 'minutes') <= 35;
  const isSecondLastRecordRecent = now.diff(secondLastRecordTime, 'minutes') <= 65;

  return isLastRecordRecent && isSecondLastRecordRecent;
};

/**
 * Checks if the latest forecast data is older than 24 hours.
 *
 * @param {Array} forecastData - An array of forecast data objects.
 * @returns {boolean} - True if the forecast is stale, otherwise false.
 */
export const isForecastStale = (forecastData) => {
  const lastForecastTimeUTC = forecastData?.at(-1)?.target_datetime_utc;

  if (!lastForecastTimeUTC) {
    return false;
  }

  const lastForecastTime = moment.utc(lastForecastTimeUTC * 1000);
  const twentyFourHoursAgo = moment().subtract(24, 'hours');

  return lastForecastTime.isBefore(twentyFourHoursAgo);
};
